import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, DropdownItemProps, DropdownProps } from 'semantic-ui-react';
import styled from 'styled-components';

import { FieldLabel } from '../../../styles';
import {
  getPropertyDetails,
  listProperties,
  PropertyWalletItemDTO
} from '../../../services/properties';
import { maskZipCode } from '../../../services/masks';

export type LenderInsuranceWalletAddressFields = {
  zipcode: string;
  street: string;
  number: string;
  complement: string;
  neighborhood: string;
  city: string;
  state: string;
};

type Props = {
  estateId?: string;
  propertyPublicId?: string;
  onSelect: (publicId: string, address: LenderInsuranceWalletAddressFields) => void;
  onClear: () => void;
};

const getAddressLabel = (item: PropertyWalletItemDTO): string => {
  if (item?.address?.formatted) return item.address.formatted;

  return [
    item?.address?.street,
    item?.address?.number,
    item?.address?.complement,
    item?.address?.neighborhood,
    item?.address?.city,
    item?.address?.state
  ]
    .filter(Boolean)
    .join(', ');
};

const getIdentification = (item: PropertyWalletItemDTO): string =>
  (item.identification || item.alias || '').trim();

const buildOption = (
  item: PropertyWalletItemDTO,
  isSelected: boolean
): DropdownItemProps => {
  const addressLabel = getAddressLabel(item);
  const identification = getIdentification(item);
  const selectedText = [identification, addressLabel].filter(Boolean).join(' — ');

  return {
    key: item.publicId,
    value: item.publicId,
    text: isSelected
      ? selectedText || 'Imóvel da carteira'
      : addressLabel || identification || 'Imóvel da carteira',
    description: !isSelected && identification && addressLabel ? identification : undefined
  };
};

const normalizeWalletSearch = (raw: string): string | undefined => {
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length === 8) return digits;
  return trimmed;
};

export const WalletTag = styled.span`
  display: inline-block;
  font-size: 0.85rem;
  color: #2185d0;
  margin-bottom: 0.5rem;
`;

const LenderInsurancePropertyWalletSearchDropdown = ({
  estateId,
  propertyPublicId,
  onSelect,
  onClear
}: Props): ReactElement | null => {
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<PropertyWalletItemDTO[]>([]);
  const [selectedItem, setSelectedItem] = useState<PropertyWalletItemDTO | null>(null);
  const requestIdRef = useRef(0);
  const onClearRef = useRef(onClear);
  onClearRef.current = onClear;

  useEffect(() => {
    if (!estateId) {
      setItems([]);
      return;
    }

    const timeout = setTimeout(async () => {
      requestIdRef.current += 1;
      const currentRequestId = requestIdRef.current;
      setIsLoading(true);

      try {
        const response = await listProperties({
          estateId,
          search: normalizeWalletSearch(search),
          page: 1,
          limit: 10
        });

        if (currentRequestId === requestIdRef.current) {
          setItems(response.items || []);
        }
      } catch (_error) {
        if (currentRequestId === requestIdRef.current) {
          setItems([]);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsLoading(false);
        }
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [estateId, search]);

  useEffect(() => {
    if (!propertyPublicId || !estateId) {
      setSelectedItem(null);
      return;
    }

    let cancelled = false;
    getPropertyDetails(propertyPublicId, estateId)
      .then(details => {
        if (cancelled) return;
        setSelectedItem({
          id: details.id ?? 0,
          publicId: details.publicId,
          identification: details.identification,
          alias: details.alias,
          address: details.address,
          type: details.type || {}
        });
      })
      .catch(() => {
        if (cancelled) return;
        setSelectedItem(null);
        onClearRef.current();
      });

    return () => {
      cancelled = true;
    };
  }, [propertyPublicId, estateId]);

  const itemsByPublicId = useMemo(() => {
    const hash = new Map<string, PropertyWalletItemDTO>();
    items.forEach(item => {
      if (item?.publicId) hash.set(item.publicId, item);
    });
    if (selectedItem?.publicId && !hash.has(selectedItem.publicId)) {
      hash.set(selectedItem.publicId, selectedItem);
    }
    return hash;
  }, [items, selectedItem]);

  const options: DropdownItemProps[] = Array.from(itemsByPublicId.values()).map(item =>
    buildOption(item, item.publicId === propertyPublicId)
  );

  if (!estateId) {
    return null;
  }

  return (
    <div style={{ marginBottom: '16px' }}>
      <FieldLabel>PESQUISAR NA CARTEIRA DE IMÓVEIS</FieldLabel>
      <Dropdown
        fluid
        selection
        clearable
        selectOnBlur={false}
        search={(opts: DropdownItemProps[]) => opts}
        loading={isLoading}
        options={options}
        value={propertyPublicId || ''}
        noResultsMessage="Nenhum imóvel encontrado."
        placeholder="Busque por CEP, bairro, logradouro ou código do imóvel"
        onSearchChange={(_event, data: DropdownProps) => {
          setSearch(String(data.searchQuery || ''));
        }}
        onChange={(_event, data: DropdownProps) => {
          const selectedId = String(data.value || '');

          if (!selectedId) {
            setSearch('');
            setSelectedItem(null);
            onClear();
            return;
          }

          const selected = itemsByPublicId.get(selectedId);
          if (!selected) return;

          onSelect(selected.publicId, {
            zipcode: maskZipCode(selected?.address?.zipcode || ''),
            street: selected?.address?.street || '',
            number: selected?.address?.number || '',
            complement: selected?.address?.complement || '',
            neighborhood: selected?.address?.neighborhood || '',
            city: selected?.address?.city || '',
            state: selected?.address?.state || ''
          });
        }}
      />
    </div>
  );
};

export default LenderInsurancePropertyWalletSearchDropdown;
